import { useCallback, useMemo, useState } from "react";
import {
  ALL_GROUP_KEY,
  Box,
  DataTable,
  Text,
  WidgetEmpty,
  useWidgetSurface,
  type BulkAction,
  type DataTableGroupType,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import {
  GroupQueryProvider,
  LocalGroupProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import { useIssueColumns } from "#app/components/issues/issue-columns";
import {
  AGE_GROUPS,
  ISSUE_GROUP_SERVER_KEY,
  UNASSIGNED_DIMENSION_LABEL,
  UNASSIGNED_GROUP_KEY,
  buildGroupWhere,
  type IssueGroupDimension,
} from "#app/components/issues/issue-grouping";
import {
  BulkStageContent,
  BulkAssigneeContent,
  type BulkIssueMutations,
} from "#app/components/issues/issue-bulk-actions";
import { useIssueView } from "#app/components/issue-view";
import { UpdateAgeDialog } from "#app/components/age/update-age-dialog";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { useIssueListMutations } from "#app/hooks/issue-list/use-issue-list-mutations";
import { useRefetchIssueGroups } from "#app/hooks/issue-list/use-refetch-issue-groups";
import { useDashboardScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";
import { canEdit } from "#app/lib/utils/access-level";
import { AnalyticsScope, AnalyticsView } from "#app/lib/analytics";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { combineWhereConditions } from "#app/lib/query-utils";
import { type IssueRow, IssuesDocument, type SortDirection, type StageOption } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

export type IssueTableGroupBy = "none" | "stage" | "workflow" | "age" | "issueType";

/**
 * `IssueTable` — the shared presentational table every issue-listing card renders:
 * the real library `DataTable` driven by the SHARED issue columns (`useIssueColumns`),
 * so each cell routes through `#app/components/entity` and the rows stay live (open, change
 * stage, edit assignees, age dialog) — never a hand-rolled table.
 *
 * It owns everything downstream of the rows: stage resolution, the open/stage/assignee
 * handlers, the age dialog, the scope-aware bulk actions, and the width-driven column
 * set. Two source modes, one paint path:
 *
 * - **Query mode** (`query`, used by `entity-list`): every group is its own windowed
 *   server query (`GroupQueryProvider` over the shared `Issues` document + the shared
 *   `buildGroupWhere` selectors — the exact machinery behind the full-page tables).
 *   The table reaches EVERY row in scope through the DataTable's windowed scroll;
 *   nothing is ever a capped bulk chunk. Grouped mode resolves its group set from
 *   the exact server `issueGroupCounts` aggregation.
 * - **Local mode** (`rows`, used by `favorites-list`): a fully-materialized bounded
 *   set (the starred issues) pages through `LocalGroupProvider`, the local twin.
 *
 * Bulk actions read the ambient `DashboardScope`: move-to-stage only under a
 * single-workflow dashboard (a cross-workflow/unscoped selection has no single stage
 * target — so it's absent on `favorites-list`); assignees for any scope.
 */
export type IssueTableQuery = {
  /** The fully-folded base `where` — scope + card Filters + search. The table
   *  derives every group's predicate from it via `buildGroupWhere`. */
  where: Record<string, unknown> | undefined;
  /** Server ordering for every group's paged fetch. */
  orderBy: Record<string, SortDirection>;
};

export type IssueTableProps = Omittable<{
  /** Query mode: windowed per-group server paging over the whole scoped set. */
  query?: IssueTableQuery;
  /** Local mode: the fully-materialized set (favorites). Ignored when `query` is set. */
  rows?: IssueRow[];
  /** Local-mode initial loading; query mode derives its own. */
  loading?: boolean;
  /** Local mode only: semantic identity of `rows` (query variables + client
   *  narrowing). Flips reset each group's scroll window. */
  variablesKey?: string;
  /** Live span width in grid columns — narrow cards drop to name + stage. */
  w: number;
  /** Refetch used by the move-to-stage bulk commit. Query mode defaults to
   *  refetching the shared issue documents; local readers may omit it. */
  refetch?: () => Promise<unknown>;
  emptyIcon: React.ComponentProps<typeof WidgetEmpty>["icon"];
  emptyTitle: string;
  emptyDescription?: string;
  /** Header label for the single (all-rows) group. */
  groupLabel?: string;
  groupBy?: IssueTableGroupBy;
  /** Personal-home treatment: fewer columns and no bulk-selection chrome. */
  home?: boolean;
}>;

/** Columns the card keeps at a given width — name always leads (it fills). */
const WIDE_COLUMN_IDS = new Set(["name", "stage", "assignees", "age"]);
const NARROW_COLUMN_IDS = new Set(["name", "stage"]);
const HOME_COLUMN_IDS = new Set(["name", "stage", "age"]);

/** Relay-connection accessor for the shared `Issues` document. */
const getIssuesConnection = (data: any) => data?.issues;
const EMPTY_ISSUES: IssueRow[] = [];
const PAGE_LIMIT = 50;

/** A partitioned slice of the card's rows — the shape of one table group. */
type IssueBucket = { key: string; label: string; data: IssueRow[] };

/** One query-mode group: its key/label, exact server total, and folded `where`. */
type QueryGroupDef = {
  key: string;
  label: string;
  totalCount: number;
  where: Record<string, unknown> | undefined;
};

function makeBucket(key: string, label: string, data: IssueRow[]): IssueBucket {
  return { key, label, data };
}

function partitionByKey(rows: IssueRow[], getKey: (row: IssueRow) => string) {
  const buckets = new Map<string, IssueRow[]>();
  for (const row of rows) {
    const key = getKey(row);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(row);
    } else {
      buckets.set(key, [row]);
    }
  }
  return buckets;
}

export function IssueTable({
  query,
  rows,
  loading = false,
  variablesKey = "",
  w,
  refetch,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  groupLabel = "Projects",
  groupBy = "none",
  home = false,
}: IssueTableProps) {
  const isQueryMode = query != null;
  const localRows = rows ?? EMPTY_ISSUES;
  // The surface the enclosing widget card painted — the table's opaque parts
  // must match it (an elevated tile and a page-flow card fill differently in
  // dark mode).
  const widgetSurface = useWidgetSurface();

  // Stage labels resolve from every workspace stage — the scope can span
  // workflows, so the Stage cell must never fall back to "Unknown".
  const { stages: workspaceStages, workflowMap, issueTypeMap } = useWorkspaceData();
  // `useWorkspaceData()` stages ARE the stage row — no re-shaping.
  const availableStages: StageOption[] = workspaceStages;
  const stageById = useMemo(() => {
    const map = new Map<string, StageOption>();
    for (const stage of availableStages) map.set(stage.stageId, stage);
    return map;
  }, [availableStages]);

  // Live row cells — the same handlers as the list view.
  const { openIssue } = useIssueView();
  const handleOpenIssue = useCallback(
    (issue: IssueRow) => openIssue(issue),
    [openIssue],
  );
  const { changeStage, updateAssignees, bulk } = useIssueListMutations();
  const bulkMutations: BulkIssueMutations = bulk;
  const [ageDialogIssue, setAgeDialogIssue] = useState<IssueRow | null>(null);
  const handleAgePress = useCallback((issue: IssueRow) => setAgeDialogIssue(issue), []);

  // Bulk actions mirror the list view (and the widget drilldown): move-to-stage
  // only under a single-workflow dashboard (a cross-workflow selection has no
  // single stage target); assignees for any scope. Passing `bulkActions` is
  // what surfaces the row-hover checkbox + select-all + the bulk actions bar.
  // The card loads its whole window client-side, so commits run on selected ids.
  const scope = useDashboardScope();
  const analyticsScope =
    scope?.entity?.type === "workflow"
      ? AnalyticsScope.Workflow
      : scope?.entity?.type === "team"
        ? AnalyticsScope.Team
        : AnalyticsScope.Member;
  const scopeWorkflowId = scope?.entity?.type === "workflow" ? scope.entity.id : undefined;
  const refetchIssueGroups = useRefetchIssueGroups();
  const refetchGroups = useMemo(
    () => refetch ?? (isQueryMode ? refetchIssueGroups : async () => {}),
    [refetch, isQueryMode, refetchIssueGroups],
  );
  const bulkActions = useMemo<BulkAction<IssueRow>[]>(() => {
    const actions: BulkAction<IssueRow>[] = [];

    if (scopeWorkflowId) {
      actions.push({
        id: "change-stage",
        type: "popover",
        label: "Move to stage",
        icon: "layers",
        contentProps: { padding: "none" },
        content: ({ selectedRows, commit }) => (
          <BulkStageContent
            view={AnalyticsView.WidgetView}
            scope={analyticsScope}
            workflowId={selectedRows[0]?.workflowId ?? scopeWorkflowId}
            commit={commit}
            bulkMutations={bulkMutations}
            refetchGroups={refetchGroups}
            availableStages={availableStages}
            selectedCount={selectedRows.length}
          />
        ),
      });
    }

    actions.push({
      id: "set-assignees",
      type: "popover",
      label: "Assignees",
      icon: "users",
      contentProps: { padding: "none", width: 384 },
      content: ({ selectedRows }) => (
        <BulkAssigneeContent
          view={AnalyticsView.WidgetView}
          scope={analyticsScope}
          bulkMutations={bulkMutations}
          selectedRows={selectedRows}
        />
      ),
    });

    return actions;
  }, [scopeWorkflowId, analyticsScope, bulkMutations, refetchGroups, availableStages]);

  const rawColumns = useIssueColumns({
    availableStages,
    onOpenIssue: handleOpenIssue,
    onChangeStage: changeStage,
    onAssigneesChange: updateAssignees,
    onAgePress: handleAgePress,
  });
  const visibleIds = home ? HOME_COLUMN_IDS : w < 4 ? NARROW_COLUMN_IDS : WIDE_COLUMN_IDS;
  const columns = useMemo(
    () =>
      rawColumns.filter((c) => {
        const id = c.id ?? (c as { accessorKey?: string }).accessorKey;
        return id != null && visibleIds.has(String(id));
      }),
    [rawColumns, visibleIds],
  );

  const buckets = useMemo<IssueBucket[]>(() => {
    if (isQueryMode) return [];
    const rows = localRows;
    if (groupBy === "none") {
      return [makeBucket(ALL_GROUP_KEY, groupLabel, rows)];
    }

    if (groupBy === "stage") {
      const byStage = partitionByKey(rows, (row) => row.stageId || UNASSIGNED_GROUP_KEY);
      const assigned = Array.from(byStage.entries())
        .filter(([key]) => key !== UNASSIGNED_GROUP_KEY)
        .sort((a, b) => {
          const stageA = stageById.get(a[0]);
          const stageB = stageById.get(b[0]);
          return (stageA?.sortOrder ?? Number.MAX_SAFE_INTEGER) - (stageB?.sortOrder ?? Number.MAX_SAFE_INTEGER);
        })
        .map(([key, data]) => makeBucket(key, stageById.get(key)?.name ?? key, data));
      const unassigned = byStage.get(UNASSIGNED_GROUP_KEY);
      return unassigned
        ? [...assigned, makeBucket(UNASSIGNED_GROUP_KEY, UNASSIGNED_DIMENSION_LABEL.stage, unassigned)]
        : assigned;
    }

    if (groupBy === "workflow") {
      const byWorkflow = partitionByKey(rows, (row) => row.workflowId || UNASSIGNED_GROUP_KEY);
      const named = Array.from(byWorkflow.entries())
        .filter(([key]) => key !== UNASSIGNED_GROUP_KEY)
        .map(([key, data]) => makeBucket(key, workflowMap.get(key)?.name ?? key, data))
        .sort((a, b) => a.label.localeCompare(b.label));
      const unassigned = byWorkflow.get(UNASSIGNED_GROUP_KEY);
      return unassigned
        ? [...named, makeBucket(UNASSIGNED_GROUP_KEY, UNASSIGNED_DIMENSION_LABEL.workflow, unassigned)]
        : named;
    }

    if (groupBy === "age") {
      const byAge = partitionByKey(rows, (row) => row.age?.status ?? UNASSIGNED_GROUP_KEY);
      return AGE_GROUPS.map(({ key, label }) => {
        const data = byAge.get(key);
        return data ? makeBucket(key, label, data) : null;
      }).filter((bucket): bucket is IssueBucket => bucket != null);
    }

    const byType = partitionByKey(rows, (row) => row.issueTypeId ?? UNASSIGNED_GROUP_KEY);
    const named = Array.from(byType.entries())
      .filter(([key]) => key !== UNASSIGNED_GROUP_KEY)
      .map(([key, data]) => {
        const issueTypeName = data[0]?.issueType?.name ?? issueTypeMap.get(key)?.name ?? key;
        return makeBucket(key, issueTypeName, data);
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    const unassigned = byType.get(UNASSIGNED_GROUP_KEY);
    return unassigned
      ? [...named, makeBucket(UNASSIGNED_GROUP_KEY, UNASSIGNED_DIMENSION_LABEL.issueType, unassigned)]
      : named;
  }, [isQueryMode, localRows, groupBy, groupLabel, stageById, workflowMap, issueTypeMap]);

  // Every group pages through its own provider (mounted below); the table
  // groups read the reported windows, mirroring the full-page tables.
  const { states, registerState, getGroupProps } = useGroupStates<IssueRow>();
  const groupContext = `${variablesKey}|${groupBy}`;

  // Query mode, grouped: the group SET comes from the exact server aggregation
  // (`issueGroupCounts`) over the same folded `where` — never from a loaded
  // sample. Each non-empty group then gets its own windowed query.
  const queryDimension: IssueGroupDimension | null =
    isQueryMode && groupBy !== "none" ? (groupBy as IssueGroupDimension) : null;
  const groupCounts = useIssueGroupCounts({
    where: queryDimension ? (query?.where ?? {}) : undefined,
    groupBy: queryDimension ? ISSUE_GROUP_SERVER_KEY[queryDimension] : undefined,
  });
  const queryGroupDefs = useMemo<QueryGroupDef[] | null>(() => {
    if (!queryDimension) return null;
    if (!groupCounts.ready) return [];
    const stageOrder = new Map(availableStages.map((s, i) => [s.stageId, i]));
    const ageOrder = new Map(AGE_GROUPS.map((g, i) => [g.key, i]));
    const labelOf = (key: string): string => {
      if (key === UNASSIGNED_GROUP_KEY) return UNASSIGNED_DIMENSION_LABEL[queryDimension];
      switch (queryDimension) {
        case "stage":
          return stageById.get(key)?.name ?? key;
        case "workflow":
          return workflowMap.get(key)?.name ?? key;
        case "age":
          return AGE_GROUPS.find((g) => g.key === key)?.label ?? key;
        case "issueType":
          return issueTypeMap.get(key)?.name ?? key;
        default:
          return key;
      }
    };
    const defs = Object.entries(groupCounts.counts)
      .filter(([, total]) => total > 0)
      .map(([key, total]) => ({
        key,
        label: labelOf(key),
        totalCount: total,
        where: combineWhereConditions(
          [query?.where, buildGroupWhere(queryDimension, key)].filter(
            (c): c is Record<string, unknown> => c != null,
          ),
        ),
      }));
    defs.sort((a, b) => {
      const unassigned =
        Number(a.key === UNASSIGNED_GROUP_KEY) - Number(b.key === UNASSIGNED_GROUP_KEY);
      if (unassigned !== 0) return unassigned;
      if (queryDimension === "stage") {
        return (
          (stageOrder.get(a.key) ?? Number.MAX_SAFE_INTEGER) -
          (stageOrder.get(b.key) ?? Number.MAX_SAFE_INTEGER)
        );
      }
      if (queryDimension === "age") {
        return (
          (ageOrder.get(a.key) ?? Number.MAX_SAFE_INTEGER) -
          (ageOrder.get(b.key) ?? Number.MAX_SAFE_INTEGER)
        );
      }
      return a.label.localeCompare(b.label);
    });
    return defs;
  }, [
    queryDimension,
    groupCounts.ready,
    groupCounts.counts,
    availableStages,
    stageById,
    workflowMap,
    issueTypeMap,
    query?.where,
  ]);

  const groups = useMemo<DataTableGroupType<IssueRow>[]>(() => {
    if (isQueryMode) {
      if (queryGroupDefs) {
        return queryGroupDefs.map((def) => ({
          key: def.key,
          label: def.label,
          totalCount: states.get(def.key)?.total ?? def.totalCount,
          ...getGroupProps(def.key),
        }));
      }
      return [
        {
          key: ALL_GROUP_KEY,
          label: groupLabel,
          totalCount: states.get(ALL_GROUP_KEY)?.total,
          ...getGroupProps(ALL_GROUP_KEY),
        },
      ];
    }
    return buckets.map((bucket) => ({
      key: bucket.key,
      label: bucket.label,
      totalCount: states.get(bucket.key)?.total,
      ...getGroupProps(bucket.key),
    }));
  }, [isQueryMode, queryGroupDefs, groupLabel, buckets, states, getGroupProps]);

  const renderGroupTitle = useCallback(
    (groupKey: string) => {
      if (groupKey === UNASSIGNED_GROUP_KEY) {
        return (
          <Text size="sm" variant="muted">
            {UNASSIGNED_DIMENSION_LABEL[groupBy as keyof typeof UNASSIGNED_DIMENSION_LABEL]}
          </Text>
        );
      }

      if (groupBy === "stage") {
        const stage = stageById.get(groupKey);
        if (!stage) return groupKey;
        return (
          <Box direction="row" align="center" gap="sm" overflow="hidden">
            <EntityTag
              entity={{ kind: "stage", id: stage.stageId, stage }}
              interactive={false}
            />
          </Box>
        );
      }

      if (groupBy === "workflow") {
        const workflow = workflowMap.get(groupKey);
        if (!workflow) return groupKey;
        return (
          <Box direction="row" align="center" gap="sm" overflow="hidden">
            <EntityTag
              entity={{ kind: "workflow", id: workflow.workflowId, workflow }}
              interactive={false}
            />
          </Box>
        );
      }

      if (groupBy === "issueType") {
        const issueType = issueTypeMap.get(groupKey);
        if (!issueType) return groupKey;
        return (
          <Box direction="row" align="center" gap="sm" overflow="hidden">
            <EntityTag
              entity={{ kind: "issueType", id: issueType.issueTypeId, issueType }}
              size="md"
              interactive={false}
            />
          </Box>
        );
      }

      return groupKey;
    },
    [groupBy, stageById, workflowMap, issueTypeMap],
  );

  // Query mode derives its own loading/empty from the group states + counts;
  // local mode keeps the caller's `loading` over the materialized rows.
  const allState = states.get(ALL_GROUP_KEY);
  const tableLoading = isQueryMode
    ? queryDimension
      ? !groupCounts.ready
      : (allState?.loading ?? true)
    : loading;
  const isEmpty = isQueryMode
    ? queryDimension
      ? groupCounts.ready && (queryGroupDefs?.length ?? 0) === 0
      : allState != null && !allState.loading && allState.total === 0
    : !loading && localRows.length === 0;
  if (isEmpty) {
    return <WidgetEmpty icon={emptyIcon} title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <Box direction="column" fill="flex" overflow="hidden">
      {isQueryMode && !queryDimension ? (
        <GroupQueryProvider<IssueRow>
          groupKey={ALL_GROUP_KEY}
          document={IssuesDocument}
          variables={{ where: query.where, orderBy: query.orderBy, limit: PAGE_LIMIT }}
          getConnection={getIssuesConnection}
          onStateChange={registerState}
        />
      ) : null}
      {isQueryMode && queryDimension
        ? (queryGroupDefs ?? []).map((def) => (
            <GroupQueryProvider<IssueRow>
              key={def.key}
              groupKey={def.key}
              document={IssuesDocument}
              variables={{ where: def.where, orderBy: query.orderBy, limit: PAGE_LIMIT }}
              getConnection={getIssuesConnection}
              onStateChange={registerState}
            />
          ))
        : null}
      {!isQueryMode
        ? buckets.map((bucket) => (
            <LocalGroupProvider<IssueRow>
              key={bucket.key}
              groupKey={bucket.key}
              rows={bucket.data}
              contextKey={groupContext}
              onStateChange={registerState}
            />
          ))
        : null}
      <Box direction="column" fill="flex" overflow="hidden">
        <DataTable
          // The table is inside a widget card, so its opaque parts — sticky
          // header, pinned cells, row washes — paint whatever the card
          // painted, not the page.
          surface={widgetSurface}
          columns={columns}
          groups={groups}
          getRowId={(row) => row.issueId}
          loading={tableLoading}
          stickyHeader
          density="regular"
          fillColumnId="name"
          groupTitle={
            groupBy === "stage" || groupBy === "workflow" || groupBy === "issueType"
              ? renderGroupTitle
              : undefined
          }
          // The card body is full-bleed (`bleed: true`), so restore the content
          // inset here: row backgrounds, dividers, and the selection wash reach
          // the card edge while cell text stays padded.
          paddingX="md"
          paddingXAlign="content"
          bulkActions={home ? undefined : bulkActions}
          canSelectRow={home ? undefined : (issue) => canEdit(issue.viewerAccessLevel)}
        />
      </Box>
      {ageDialogIssue && (
        <UpdateAgeDialog
          open
          onOpenChange={(open) => {
            if (!open) setAgeDialogIssue(null);
          }}
          issueId={ageDialogIssue.issueId}
          age={ageDialogIssue.age}
          stageId={ageDialogIssue.stageId}
        />
      )}
    </Box>
  );
}
